import {useStopAllTreatmentSummary} from '@/modules/patients/hooks/use-stop-all-treatment-summary'
import {useZero} from '@/shared/integrations/zero/use-zero'
import {Box, Button, ButtonGroup, HStack, Text, Textarea, useDialogContext, VStack} from '@chakra-ui/react'
import {Patient} from '@cypro/engine/schema'
import {DialogBody, DialogFooter, DialogHeader} from '@cypro/ui/components'
import {toaster} from '@cypro/ui/toaster'
import {useTranslate} from '@tolgee/react'
import {useState} from 'react'

export interface StopAllTreatmentDialogProps {
  patient: Patient
}

export function StopAllTreatmentDialog({patient}: StopAllTreatmentDialogProps) {
  const {t} = useTranslate('common')
  const context = useDialogContext()
  const zero = useZero()
  const [note, setNote] = useState('')
  const [isCancelling, setIsCancelling] = useState(false)
  const {activeProtocols, individualPrescriptions, prescriptionsToCancel} = useStopAllTreatmentSummary(patient.id)

  function onKeep() {
    context.setOpen(false)
  }

  function onCancel() {
    setIsCancelling(true)
    void zero.attemptMutation(
      zero.mutators.patients.stopAllTreatment({patientId: patient.id, reason: note || undefined}),
      async mutation => {
        const data = await mutation.server
        if (!data) {
          setIsCancelling(false)
          return
        }
        context.setOpen(false)
        toaster.success({title: t('success'), description: t('patient_updated_successfully')})
      }
    )
  }

  return (
    <>
      <DialogHeader>
        <Text fontSize="lg" fontWeight="semibold">
          {t('cancel_future_prescriptions_confirm', 'Cancel future prescriptions?')}
        </Text>
      </DialogHeader>

      <DialogBody minHeight="none">
        <VStack align="stretch" gap="4" width="100%">
          <Text fontSize="sm" color="fg.muted">
            {t(
              'stop_all_treatment_description',
              'The patient will not continue any treatment. Active protocols are ended and their history is kept.'
            )}
          </Text>

          <Box bg="bg.subtle" borderRadius="md" borderWidth="1px" borderColor="border" p="4">
            <VStack align="stretch" gap="2">
              <HStack gap="2">
                <Text fontSize="sm" color="fg.muted">
                  {t('patient')}:
                </Text>
                <Text fontSize="sm" fontWeight="semibold">
                  {patient.fullName}
                </Text>
              </HStack>
              <HStack gap="2">
                <Text fontSize="sm" color="fg.muted">
                  {t('active_protocols', 'Active protocols')}:
                </Text>
                <Text fontSize="sm" fontWeight="semibold">
                  {activeProtocols}
                </Text>
              </HStack>
              <HStack gap="2">
                <Text fontSize="sm" color="fg.muted">
                  {t('individual_prescriptions', 'Individual prescriptions')}:
                </Text>
                <Text fontSize="sm" fontWeight="semibold">
                  {individualPrescriptions}
                </Text>
              </HStack>
              <HStack gap="2">
                <Text fontSize="sm" color="fg.muted">
                  {t('prescriptions_to_cancel', 'Prescriptions to cancel')}:
                </Text>
                <Text fontSize="sm" fontWeight="semibold">
                  {prescriptionsToCancel}
                </Text>
              </HStack>
            </VStack>
          </Box>

          <Text fontSize="sm" color="fg.muted">
            {t(
              'stop_all_treatment_cancellation_notice',
              'All prescriptions of this patient that were not administered are cancelled.'
            )}
          </Text>

          <Box>
            <Text fontWeight="medium" marginBottom="2">
              {t('note_optional', 'Note (optional)')}
            </Text>
            <Textarea
              placeholder={t('recorded_in_patient_history', 'Recorded in the patient history')}
              value={note}
              onChange={e => {
                setNote(e.target.value)
              }}
              rows={3}
            />
          </Box>
        </VStack>
      </DialogBody>

      <DialogFooter>
        <ButtonGroup size="md" width="100%" justifyContent="flex-end">
          <Button variant="outline" colorPalette="gray" onClick={onKeep} disabled={isCancelling}>
            {t('keep_prescriptions', 'Keep prescriptions')}
          </Button>
          <Button colorPalette="red" onClick={onCancel} loading={isCancelling}>
            {t('cancel_prescriptions', 'Cancel prescriptions')}
          </Button>
        </ButtonGroup>
      </DialogFooter>
    </>
  )
}
